import { OpenAPI } from '@beeboard/common';
import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import { useProfileStore } from '@/store/profileStore';
import { useSocketStore } from '@/store/socketStore';

OpenAPI.BASE = import.meta.env.VITE_API_ENDPOINT as string;
OpenAPI.HEADERS = { 'x-client-type': 'browser' };
OpenAPI.TOKEN = async () => {
  const authStore = useAuthStore();
  // await refresh when the bearer token is invalid
  if (!authStore.validBearer()) {
    console.log('invalid token, await refresh');
    await authStore.refreshToken();
  }

  const { access } = authStore.decoded;
  const validNextSeconds = access.exp - Math.round(Date.now() / 1000);

  // refresh lazy when the bearer token is about to expire
  if (validNextSeconds < 15) {
    console.log('token about to expire, lazy refresh');
    await authStore.refreshToken();
  }

  return authStore.tokens.access;
};

// Guards against re-entering the session-expired path once it has started:
// logout() itself makes HTTP calls that may also 401 while we're already
// tearing the session down.
let sessionExpirationInFlight = false;

export function handleSessionExpired() {
  if (sessionExpirationInFlight) {
    return;
  }
  sessionExpirationInFlight = true;

  try {
    useSocketStore().destroySocketConnection();
  } catch (err) {
    console.error('[axiosInterceptors] destroySocketConnection failed', err);
  }

  useAuthStore()
    .logout()
    .catch((err) => {
      console.error('[axiosInterceptors] logout failed', err);
    });
}

function interceptorErrorHandler(error: unknown) {
  useProfileStore().decrementRequest();

  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401 && !sessionExpirationInFlight) {
    handleSessionExpired();
  }

  console.error(error);

  return Promise.reject(error);
}

axios.interceptors.request.use(function (config) {
  useProfileStore().incrementRequest();

  return config;
}, interceptorErrorHandler);

axios.interceptors.response.use(function (response) {
  useProfileStore().decrementRequest();

  return response;
}, interceptorErrorHandler);
